import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Lightbulb, CheckCircle, ArrowLeft, Download, Video, Star, Target, Users, ExternalLink } from 'lucide-react';
import { StepCard, VideoCard, InfoCard, ExampleCard } from './SectionComponents';

const CaseStudySection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {
  const [checkedItems, setCheckedItems] = useState({});

  const keyPrinciples = [
    {
      emoji: "🧠",
      title: "Show your logic",
      description: "Walk through your thinking, assumptions, and how you reached conclusions"
    },
    {
      emoji: "✨",
      title: "Add value beyond the brief",
      description: "Don't just answer what's asked — bring additional insights or suggestions"
    },
    {
      emoji: "🎯",
      title: "Make the extra effort",
      description: "Polish your presentation, format clearly, and include supporting materials"
    }
  ];

  const qualityMarkers = [
    "Have clear structure and logical flow",
    "Show research and data-driven thinking",
    "Include specific, actionable recommendations", 
    "Demonstrate understanding of the business context",
    "Go beyond the brief with bonus insights or materials",
    "Are well-presented and easy to follow",
    "Include bonus elements where relevant (e.g. comms drafts, outreach mockups, process flows)"
  ];

  const bestPracticeExamples = [
    {
      category: "General commercial / strategy",
      examples: [
        {
          title: "Autogen AI: commercial targeting",
          description: "A comprehensive approach to identifying and reaching target customers",
          slides: "https://drive.google.com/file/d/186djjpEeyUbsnrj-AtP-AI0fjCGgUrli/view",
          loom: "https://www.loom.com/share/ad0cf917943c4ed59c94285fa893b7a0"
        }
      ]
    },
    {
      category: "Operational thinking",
      examples: [
        {
          title: "Sightline Climate: workshop planning",
          description: "Strategic approach to event planning and operational execution",
          loom: "https://www.loom.com/share/0d6ee9523bd44694a43b51a70d613d5d"
        }
      ]
    },
    {
      category: "Brand & comms / marketing case studies",
      examples: [
        {
          title: "&SISTERS: optimising online presence",
          description: "Complete brand and digital strategy optimization",
          loom: "https://www.loom.com/share/0f16fcdb585b4894bc1091e5b18ceebd?sid=85b67daf-d960-4b86-9762-92cdf033df91",
          slides: "https://drive.google.com/file/d/1tHlRYFdVSgt0fJBl9ufj0JkVj7dlashr/view?usp=sharing"
        },
        {
          title: "Jumpstart: community event email",
          description: "Community engagement and email marketing strategy",
          instructions: "https://www.notion.so/Jumpstart-Community-Lead-Written-task-b5d7e58341af4b50b44b406b1284c75e?pvs=21",
          loom: "https://www.loom.com/share/0f16fcdb585b4894bc1091e5b18ceebd"
        }
      ]
    }
  ];

  const bonusTools = [
    {
      title: "Excellent email inspiration",
      description: "Explore great comms & email design",
      url: "https://reallygoodemails.com/",
      emoji: "📧"
    },
    {
      title: "PowerPoint slide template",
      description: "Use this if you want to structure your case in slide form",
      url: "https://drive.google.com/file/d/186djjpEeyUbsnrj-AtP-AI0fjCGgUrli/view",
      emoji: "📊"
    }
  ];

  const checklist = [
    "I understand the business context and target audience",
    "My recommendations are specific and actionable",
    "I've shown my working and reasoning clearly",
    "I've researched beyond the brief where relevant",
    "My presentation is well-structured and easy to follow",
    "I've included supporting materials or bonus insights",
    "I've proofread and formatted my submission professionally"
  ];

  const handleCheckChange = (index) => {
    setCheckedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <div className="w-24 h-24 bg-gradient-to-br from-[#02B491] to-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-lg shadow-emerald-500/20">
              <FileText className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-6xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-6">
              Nailing your take-home case study
            </h1>
            <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
              Turn your case study into a standout submission that shows strategic thinking and attention to detail.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-slate-700 rounded-2xl font-semibold hover:bg-slate-50 transition-all duration-300 shadow-lg border border-slate-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">

          <StepCard title="📋 Rules of thumb & what good looks like">
            <div className="space-y-10">
              <div>
                <h4 className="font-bold text-slate-900 mb-8 text-xl">Three principles to always keep in mind:</h4>
                <div className="grid gap-8 md:grid-cols-3">
                  {keyPrinciples.map((principle, index) => (
                    <div key={index} className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg hover:shadow-gray-900/5 transition-all duration-300">
                      <h5 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2">
                        <span className="text-2xl">{principle.emoji}</span>
                        {principle.title}
                      </h5>
                      <p className="text-gray-600 leading-relaxed">
                        {index === 0 && (
                          <>Walk through your <span className="font-semibold text-gray-800">thinking, assumptions, and how you reached conclusions</span></>
                        )}
                        {index === 1 && (
                          <>Don't just answer what's asked — bring <span className="font-semibold text-gray-800">additional insights or suggestions</span></>
                        )}
                        {index === 2 && (
                          <>Polish your presentation, <span className="font-semibold text-gray-800">format clearly, and include supporting materials</span></>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <InfoCard
                icon={Star}
                title="Remember"
                description="These 'extra 5%' touches often make the difference between a good and great submission."
                type="tip"
              />

              <div>
                <h4 className="font-bold text-slate-900 mb-8 text-xl">So what does good look like? The best case study submissions:</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  {qualityMarkers.map((marker, i) => (
                    <div key={i} className="flex items-start space-x-4 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl p-4 border border-emerald-200/50">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                      <span className="text-emerald-800 leading-relaxed">{marker}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="Best-in-class examples & templates">
            <div className="space-y-12">
              <p className="text-slate-600 text-lg leading-relaxed">
                Explore these high-quality case study examples to see what 'great' looks like:
              </p>
              
              {bestPracticeExamples.map((category, categoryIndex) => (
                <div key={categoryIndex} className="space-y-8">
                  <h4 className="text-2xl font-bold text-slate-900 pb-4 border-b border-slate-200">{category.category}</h4>
                  <div className="space-y-8">
                    {category.examples.map((example, exampleIndex) => (
                      <div key={exampleIndex} className="space-y-6">
                        <div className="bg-white rounded-2xl p-8 border border-gray-200">
                          <h5 className="font-bold text-gray-900 mb-3 text-xl">{example.title}</h5>
                          {example.description && (
                            <p className="text-gray-600 mb-6 text-lg leading-relaxed">{example.description}</p>
                          )}
                          
                          <div className="flex flex-wrap gap-4 mb-6">
                            {example.slides && (
                              <a 
                                href={example.slides} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300"
                              >
                                <Download className="w-5 h-5 mr-2" />
                                Download Slides
                                <ExternalLink className="w-4 h-4 ml-2 opacity-75" />
                              </a>
                            )}
                            {example.instructions && (
                              <a 
                                href={example.instructions} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300"
                              >
                                <FileText className="w-5 h-5 mr-2" />
                                View Brief
                                <ExternalLink className="w-4 h-4 ml-2 opacity-75" />
                              </a>
                            )}
                          </div>
                        </div>
                        
                        {example.loom && (
                          <div>
                            <h6 className="font-semibold text-slate-900 mb-4 text-lg">Watch the walkthrough:</h6>
                            <VideoCard 
                              title={`${example.title} - Walkthrough`}
                              url={example.loom}
                              description="Detailed explanation of the approach and key insights"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </StepCard>

          <StepCard title="Bonus tools & inspiration">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                Additional resources to help elevate your case study submissions:
              </p>
              
              <div className="grid gap-6 md:grid-cols-2">
                {bonusTools.map((tool, index) => (
                  <ExampleCard
                    key={index}
                    emoji={tool.emoji}
                    title={tool.title}
                    links={[{
                      url: tool.url,
                      text: "Explore Resource",
                      icon: ExternalLink,
                      className: "bg-gradient-to-r from-[#02B491] to-emerald-600 text-white hover:shadow-lg hover:shadow-emerald-500/20"
                    }]}
                  >
                    <p className="text-slate-600 leading-relaxed">{tool.description}</p>
                  </ExampleCard>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="Case study submission checklist">
            <div className="space-y-6">
              <p className="text-slate-600 text-lg leading-relaxed">
                Before submitting your case study, make sure you've covered these essential elements:
              </p>
              
              <div className="space-y-4">
                {checklist.map((item, i) => (
                  <label key={i} className="flex items-start space-x-4 cursor-pointer group">
                    <input 
                      type="checkbox" 
                      checked={checkedItems[i] || false}
                      onChange={() => handleCheckChange(i)}
                      className="w-5 h-5 text-[#02B491] rounded border-2 border-slate-300 focus:ring-[#02B491] focus:ring-2 mt-1"
                    />
                    <span className={`leading-relaxed transition-colors ${
                      checkedItems[i] ? 'text-slate-500 line-through' : 'text-slate-700 group-hover:text-slate-900'
                    }`}>
                      {item}
                    </span>
                  </label>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-2xl border border-emerald-200/50">
                <p className="text-emerald-900 font-semibold">
                  🎯 Pro tip: The best submissions often include bonus materials like draft emails, process flows, or additional research that wasn't specifically requested.
                </p>
              </div>
            </div>
          </StepCard>
        </div>
      </div>
      
      {hasNext && (
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12 pb-20">
          <div className="text-center">
            <button
              onClick={onNext}
              className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
            >
              Continue to: {nextSectionTitle}
              <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseStudySection;